/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { useQualityInput } from "../../store/useQualityInput";
import { camelToText } from "../../utils/helpers";
import dayjs from "dayjs";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { getWeekRange } from "../../services/dateService";

export const MaterialCompositionPlastics = React.memo(() => {
  /** Hooks */
  const { getMaterialCompositionPlastics, materialCompositionPlastics } =
    useQualityInput();
  /** State */
  const [period, setPeriod] = useState<
    "Daily" | "Weekly" | "Monthly" | "Yearly"
  >("Monthly");
  const [weeklyRange, setWeeklyRange] = useState(new Date());
  const [monthRange, setMonthRange] = useState(new Date());
  const [yearRange, setYearRange] = useState(new Date());
  const { startOfWeek, endOfWeek } = getWeekRange(weeklyRange);

  /** Callbacks */
  const getLastWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() - 7)));
  const getNextWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() + 7)));

  const getLastMonth = () =>
    setMonthRange(dayjs(monthRange).subtract(1, "month").toDate());
  const getNextMonth = () =>
    setMonthRange(dayjs(monthRange).add(1, "month").toDate());

  /** Effects */
  useEffect(() => {
    if (period === "Weekly") {
      getMaterialCompositionPlastics(weeklyRange, "weekly");
    } else if (period === "Monthly") {
      getMaterialCompositionPlastics(monthRange, "monthly");
    } else if (period === "Yearly") {
      getMaterialCompositionPlastics(yearRange, "yearly");
    }
  }, [weeklyRange, monthRange, yearRange, period]);

  return (
    <div className="p-7 w-full bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-10">
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-row items-start justify-start">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
            Material Composition Plastics
          </p>
          {/* <Tooltip content="Total stream kg / (Total active users * years) per stream">
            <img
              alt="info-icon"
              src={require("../../assets/icons/info/information.png")}
              className="ml-3 w-[25px] h-[25px] mt-2"
            />
          </Tooltip> */}
        </div>
        <div className="flex flex-col sm:flex-row items-start justify-start mb-12">
          {period === "Weekly" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
              </div>
              <PrimaryButton
                size="sm"
                disabled={endOfWeek > new Date()}
                onClick={getNextWeek}
                label="Next"
              />
            </div>
          ) : null}
          {period === "Monthly" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastMonth} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {dayjs(monthRange).format("MMMM YYYY")}
              </div>
              <PrimaryButton
                size="sm"
                disabled={dayjs(monthRange).add(1, "month").isAfter(dayjs())}
                onClick={getNextMonth}
                label="Next"
              />
            </div>
          ) : null}
          <Menu>
            <MenuHandler>
              <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {period}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                disabled={period === "Weekly"}
                onClick={() => setPeriod("Weekly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Weekly
              </MenuItem>
              <MenuItem
                disabled={period === "Monthly"}
                onClick={() => setPeriod("Monthly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Monthly
              </MenuItem>
              <MenuItem
                disabled={period === "Yearly"}
                onClick={() => setPeriod("Yearly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Yearly
              </MenuItem>
            </MenuList>
          </Menu>
          {period === "Yearly" ? (
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 sm:ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {yearRange.getFullYear()}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(yearRange.setFullYear(new Date().getFullYear()))
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear()}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(
                        yearRange.setFullYear(new Date().getFullYear() - 1)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 1}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(
                        yearRange.setFullYear(new Date().getFullYear() - 2)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 2}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    setYearRange(
                      new Date(
                        yearRange.setFullYear(new Date().getFullYear() - 3)
                      )
                    )
                  }
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {new Date().getFullYear() - 3}
                </MenuItem>
              </MenuList>
            </Menu>
          ) : null}
        </div>
      </div>
      {/* @ts-ignore */}
      {Object.values(materialCompositionPlastics ?? {})?.length ? (
        <Chart
          {...streamCompositionsDonutChartData}
          height={600}
          // @ts-ignore
          options={{
            ...streamCompositionsDonutChartData?.options,
            labels: Object.keys(materialCompositionPlastics ?? {})?.map(
              (item) => camelToText(item)
            ),
            plotOptions: {
              ...streamCompositionsDonutChartData?.options?.plotOptions,
            },
          }}
          series={Object.values(materialCompositionPlastics ?? {})}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
            No data found
          </p>
        </div>
      )}
    </div>
  );
});

const streamCompositionsDonutChartData = {
  type: "pie",
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: any, opts: any) {
        return `${opts.w.globals.labels[opts.seriesIndex]}`;
      },
    },
    colors: [
      "#83EAF4",
      "#56B5B1",
      "#3C5219",
      "#002A27",
      "#8DAC83",
      "#8898B6",
      "#A8BF8A",
      "#84BAFF",
      "#93C1DA",
      "#97D2F7",
      "#142409",
      "#81CE7C",
      "#7DEA96",
      "#98E9FE",
      "#92E2DD",
      "#3F5747",
      "#3F4A14",
      "#8E9189",
      "#A199AD",
      "#A7CF85",
    ],
    legend: {
      show: true,
      position: "right",
      horizontalAlign: "left",
      fontSize: "14px",
      fontFamily: "Satoshi",
      fontWeight: 500,
      markers: {
        width: 16,
        height: 16,
        radius: 8,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
    plotOptions: {
      pie: {
        startAngle: 0,
        endAngle: 360,
        expandOnClick: true,
        offsetX: 0,
        offsetY: 0,
      },
    },
    tooltip: {
      theme: "dark",
      y: {
        formatter: function (value: number) {
          // Format tooltip value as percentage
          return `${value.toFixed(1)}%`;
        },
      },
    },
  },
};
